import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { fighterModelUrl, fighters } from './content'
import { warmCachedAsset } from './assetWarmup'

type Props = {
  playerIndex: number
  onLock?: (index: number) => Promise<void> | void
  onComplete: (index: number) => void
}

function randomInt(max: number) {
  if (max <= 1) return 0
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const sample = new Uint32Array(1)
    crypto.getRandomValues(sample)
    return sample[0] % max
  }
  return Math.floor(Math.random() * max)
}

export function pickRandomRival(playerIndex: number, rosterSize = fighters.length) {
  const pool = Array.from({ length: rosterSize }, (_, index) => index).filter((index) => index !== playerIndex)
  if (!pool.length) return playerIndex
  return pool[randomInt(pool.length)]
}

export function RandomRivalScreen({ playerIndex, onLock, onComplete }: Props) {
  const pool = useMemo(
    () => fighters.map((_, index) => index).filter((index) => index !== playerIndex),
    [playerIndex],
  )
  const qaEnemy = useMemo(() => {
    if (typeof window === 'undefined') return null
    if (window.location.hostname !== '127.0.0.1' && window.location.hostname !== 'localhost') return null
    const raw = new URLSearchParams(window.location.search).get('qaEnemy')
    if (raw === null) return null
    const index = Number(raw)
    return Number.isSafeInteger(index) && pool.includes(index) ? index : null
  }, [pool])
  const [previewIndex, setPreviewIndex] = useState(pool[0] ?? playerIndex)
  const [locked, setLocked] = useState(false)
  const [departing, setDeparting] = useState(false)
  const completeRef = useRef(onComplete)
  const lockRef = useRef(onLock)
  useEffect(() => {
    completeRef.current = onComplete
  }, [onComplete])
  useEffect(() => {
    lockRef.current = onLock
  }, [onLock])

  useEffect(() => {
    setLocked(false)
    setDeparting(false)
    if (!pool.length) {
      completeRef.current(playerIndex)
      return
    }

    // Reuse the historical network prefetch while retaining the newer onLock rig/GPU gate.
    const finalIndex = qaEnemy ?? pickRandomRival(playerIndex, fighters.length)
    void warmCachedAsset(fighterModelUrl(fighters[finalIndex])).catch(() => undefined)

    let last = qaEnemy ?? pool[0]
    let start = 0
    let cycle = 0
    let finish = 0
    let settle = 0
    let handoff = 0
    let cancelled = false

    // Paint the protocol briefly, but register the final lock deadline now.
    // If WebGL/asset disposal blocks the main thread, the finish timer is already
    // due when control returns instead of adding another full roulette duration.
    const protocolStartedAt = performance.now()
    const lockDeadlineMs = 1500
    start = window.setTimeout(() => {
      if (qaEnemy !== null) { setPreviewIndex(qaEnemy); return }
      cycle = window.setInterval(() => {
        let next = pool[randomInt(pool.length)]
        if (pool.length > 1 && next === last) next = pool[(pool.indexOf(next) + 1) % pool.length]
        last = next
        // Keep roulette previews local and deliberately low-frequency. Updating the
        // global opponent here would remount heavy GLBs; excessive local rerenders
        // also steal budget from the hard handoff deadline on saturated machines.
        setPreviewIndex(next)
      }, 135)
    }, 80)

    const finishProtocol = async () => {
      window.clearInterval(cycle)
      setPreviewIndex(finalIndex)
      setLocked(true)
      // Resolve the actual opponent while the roulette still owns the screen.
      // The selection Canvas then parses/clones/compiles that exact fighter under
      // cover, so arena ingress never pays the GLB/rig cost on its first frame.
      try { await lockRef.current?.(finalIndex) } catch { /* timeout/fallback is owned by the parent */ }
      if(cancelled)return
      settle = window.setTimeout(() => {
        if(cancelled)return
        setDeparting(true)
        handoff = window.requestAnimationFrame(() => completeRef.current(finalIndex))
      }, 90)
    }
    finish = window.setTimeout(finishProtocol, Math.max(0, lockDeadlineMs - (performance.now() - protocolStartedAt)))

    return () => {
      cancelled = true
      window.clearTimeout(start)
      window.clearInterval(cycle)
      window.clearTimeout(finish)
      window.clearTimeout(settle)
      window.cancelAnimationFrame(handoff)
    }
  }, [playerIndex, pool, qaEnemy])

  if (departing) return null
  const fighter = fighters[previewIndex] ?? fighters[playerIndex]
  return (
    <section className="random-rival-screen" data-testid="random-rival-screen" data-state={locked ? 'locked' : 'cycling'} aria-label="Random rival selection">
      <div className="random-rival-scan" />
      <span className="random-rival-kicker">CPU OPPONENT PROTOCOL</span>
      <strong style={{ '--random-accent': fighter.accent } as CSSProperties}>{fighter.name}</strong>
      <small>{fighter.title}</small>
      <div className="random-rival-meter"><i /><i /><i /><i /><i /></div>
      <p>{locked ? 'RIVAL LOCKED // PREPARING ARENA' : 'RANDOMIZING RIVAL // AI CONTROL LOCKED'}</p>
    </section>
  )
}
